// src/components/Header.jsx

import React, { useEffect, useState } from "react";

import {
  Box,
  IconButton,
  Avatar,
  Typography,
  Tooltip,
  Menu,
  MenuItem,
  Divider,
  useMediaQuery,
} from "@mui/material";

import { useTheme } from "@mui/material/styles";

import MenuIcon from "@mui/icons-material/Menu";
import LogoutIcon from "@mui/icons-material/Logout";
import AccountCircleIcon from "@mui/icons-material/AccountCircle";
import AccessTimeIcon from "@mui/icons-material/AccessTime";

import defaultLogo from "../assets/logo.png";


// ============================================================
// API
// ============================================================

const API_BASE = "https://dgps.co.in";


// ============================================================
// SCHOOL LOGO
// ============================================================

const LOGO_BASE =
  "https://dgps.co.in/public/admin/assets/img/schools/logo";


// ============================================================
// HEADER
// ============================================================

export default function Header({ onToggle }) {

  // ----------------------------------------------------------
  // USER
  // ----------------------------------------------------------

  const [userName, setUserName] = useState("");
  const [avatarSrc, setAvatarSrc] = useState("");

  // ----------------------------------------------------------
  // SCHOOL
  // ----------------------------------------------------------

  const [schoolLogo, setSchoolLogo] =
    useState(defaultLogo);

  const [schoolName, setSchoolName] =
    useState("");

  // ----------------------------------------------------------
  // MENU
  // ----------------------------------------------------------

  const [anchorEl, setAnchorEl] =
    useState(null);

  // ----------------------------------------------------------
  // SUBSCRIPTION
  // ----------------------------------------------------------

  const [subscription, setSubscription] =
    useState(null);

  const [remainingDays, setRemainingDays] =
    useState(null);

  const [subscriptionLoading, setSubscriptionLoading] =
    useState(true);

  // ----------------------------------------------------------
  // THEME
  // ----------------------------------------------------------

  const theme = useTheme();

  const isMobile =
    useMediaQuery(theme.breakpoints.down("sm"));


  // ==========================================================
  // LOAD USER + SCHOOL
  // ==========================================================

  useEffect(() => {

    try {

      // ------------------------------------------------------
      // USER NAME
      // ------------------------------------------------------

      const savedName =
        localStorage.getItem("name");

      setUserName(savedName || "");


      // ------------------------------------------------------
      // AVATAR
      // ------------------------------------------------------

      const avatar =
        localStorage.getItem("avatar");

      if (avatar) {

        setAvatarSrc(avatar);

      }


      // ------------------------------------------------------
      // SCHOOL DATA
      // ------------------------------------------------------

      const rawSchools =
        localStorage.getItem("schools");

      const savedSchoolId =
        localStorage.getItem("school_id");


      if (rawSchools) {

        const parsed =
          JSON.parse(rawSchools);


        const chosen =
          parsed.find(
            (school) =>
              String(school.id) ===
              String(savedSchoolId)
          ) || parsed[0];


        if (chosen) {

          // SCHOOL NAME

          setSchoolName(
            (chosen.name || "").toUpperCase()
          );


          // SCHOOL LOGO

          if (chosen.logo) {

            let logo =
              chosen.logo;


            if (
              !/^https?:\/\//i.test(logo) &&
              !logo.startsWith("data:")
            ) {

              logo =
                `${LOGO_BASE}/${logo.replace(
                  /^\/+/,
                  ""
                )}`;

            }


            setSchoolLogo(logo);

          }

        }

      } else {

        // ----------------------------------------------------
        // FALLBACK
        // ----------------------------------------------------

        const savedSchoolName =
          localStorage.getItem("school_name");

        const savedSchoolLogo =
          localStorage.getItem("school_logo");


        if (savedSchoolName) {

          setSchoolName(
            savedSchoolName.toUpperCase()
          );

        }


        if (savedSchoolLogo) {

          let logo =
            savedSchoolLogo;


          if (
            !/^https?:\/\//i.test(logo) &&
            !logo.startsWith("data:")
          ) {

            logo =
              `${LOGO_BASE}/${logo.replace(
                /^\/+/,
                ""
              )}`;

          }


          setSchoolLogo(logo);

        }

      }

    } catch (error) {

      console.warn(
        "Header initialization error:",
        error
      );

    }

  }, []);


  // ==========================================================
  // SCHOOL ID
  // ==========================================================

  const getSchoolId = () => {

    return localStorage.getItem(
      "school_id"
    );

  };


  // ==========================================================
  // FETCH SUBSCRIPTION
  // ==========================================================

  const fetchSubscription = async () => {

    const schoolId =
      getSchoolId();


    if (!schoolId) {

      console.warn(
        "School ID not found"
      );

      setSubscriptionLoading(false);

      return;

    }


    try {

      setSubscriptionLoading(true);


      const response =
        await fetch(
          `${API_BASE}/api/schoolsubscription/get_subscription/${schoolId}`,
          {
            method: "GET",

            headers: {
              Accept:
                "application/json",
            },
          }
        );


      if (!response.ok) {

        throw new Error(
          `HTTP ${response.status}`
        );

      }


      const result =
        await response.json();


      console.log(
        "Subscription API:",
        result
      );


      if (
        result &&
        result.status === true &&
        result.data
      ) {

        setSubscription(
          result.data
        );

      } else {

        setSubscription(null);

      }

    } catch (error) {

      console.error(
        "Subscription fetch error:",
        error
      );

      setSubscription(null);

    } finally {

      setSubscriptionLoading(false);

    }

  };


  // ==========================================================
  // LOAD SUBSCRIPTION
  // ==========================================================

  useEffect(() => {

    fetchSubscription();

  }, []);


  // ==========================================================
  // CALCULATE ONLY DAYS
  // ==========================================================

  useEffect(() => {

    if (
      !subscription ||
      !subscription.due_date
    ) {

      setRemainingDays(null);

      return;

    }


    const calculateRemainingDays = () => {

      try {

        // ----------------------------------------------------
        // TODAY
        // ----------------------------------------------------

        const now =
          new Date();


        const today =
          new Date(
            now.getFullYear(),
            now.getMonth(),
            now.getDate()
          );


        // ----------------------------------------------------
        // DUE DATE
        // ----------------------------------------------------

        const parts =
          subscription.due_date
            .split("-")
            .map(Number);


        if (parts.length !== 3) {

          setRemainingDays(null);

          return;

        }


        const dueDate =
          new Date(
            parts[0],
            parts[1] - 1,
            parts[2]
          );


        // ----------------------------------------------------
        // DIFFERENCE
        // ----------------------------------------------------

        const difference =
          dueDate.getTime() -
          today.getTime();


        const days =
          Math.ceil(
            difference /
              (1000 * 60 * 60 * 24)
          );


        setRemainingDays(
          Math.max(0, days)
        );

      } catch (error) {

        console.error(
          "Countdown calculation error:",
          error
        );

        setRemainingDays(null);

      }

    };


    // Initial calculation

    calculateRemainingDays();


    // Update every minute

    const timer =
      setInterval(
        calculateRemainingDays,
        60 * 1000
      );


    return () => {

      clearInterval(timer);

    };

  }, [subscription]);


  // ==========================================================
  // MENU
  // ==========================================================

  const handleMenuOpen = (event) => {

    setAnchorEl(
      event.currentTarget
    );

  };


  const handleMenuClose = () => {

    setAnchorEl(null);

  };


  // ==========================================================
  // LOGOUT
  // ==========================================================

  const handleLogout = () => {

    localStorage.clear();

    window.location.href =
      "https://feesbook.co.in/";

  };


  // ==========================================================
  // RENDER
  // ==========================================================

  return (

    <Box
      component="header"
      sx={{

        position: "fixed",

        top: 0,

        left: 0,

        right: 0,

        height: {
          xs: 68,
          sm: 72,
        },

        display: "flex",

        alignItems: "center",

        justifyContent: "space-between",

        px: {
          xs: 1,
          sm: 2,
          md: 2.5,
        },

        bgcolor:
          "rgba(255,255,255,0.94)",

        borderBottom:
          "1px solid rgba(37,99,235,0.12)",

        zIndex: 1400,

        boxShadow:
          "0 14px 36px rgba(23,32,51,0.12)",

        backdropFilter:
          "saturate(140%) blur(16px)",

        background:
          "linear-gradient(135deg, rgba(255,255,255,0.98) 0%, rgba(240,249,255,0.96) 46%, rgba(236,253,245,0.94) 100%)",

        overflow: "visible",

        "&::before": {

          content: '""',

          position: "absolute",

          inset: 0,

          background:
            "radial-gradient(circle at 16% 0%, rgba(37,99,235,0.1), transparent 28%), radial-gradient(circle at 86% 0%, rgba(22,163,74,0.11), transparent 26%)",

          pointerEvents: "none",

        },

        "&::after": {

          content: '""',

          position: "absolute",

          left: 0,

          right: 0,

          bottom: 0,

          height: 2,

          background:
            "#d7e3f1",

        },

      }}
    >

      {/* =====================================================
          LEFT SECTION
      ====================================================== */}

      <Box
        sx={{

          display: "flex",

          alignItems: "center",

          gap: {
            xs: 0.6,
            sm: 0.9,
          },

          flexShrink: 0,

          position: "relative",

          zIndex: 2,

        }}
      >

        {/* MENU */}

        <IconButton
          onClick={() =>
            typeof onToggle === "function" &&
            onToggle()
          }
          sx={{

            width: {
              xs: 40,
              sm: 44,
            },

            height: {
              xs: 40,
              sm: 44,
            },

            color: "#102a43",

            bgcolor:
              "rgba(37,99,235,0.08)",

            border:
              "1px solid rgba(37,99,235,0.14)",

            boxShadow:
              "0 8px 20px rgba(37,99,235,0.10)",

            "&:hover": {

              bgcolor:
                "rgba(37,99,235,0.14)",

              color: "#1d4ed8",

            },

          }}
        >

          <MenuIcon />

        </IconButton>


        {/* SCHOOL LOGO */}

        <Box
          sx={{

            width: {
              xs: 46,
              sm: 52,
            },

            height: {
              xs: 46,
              sm: 52,
            },

            p: 0.5,

            borderRadius: 2,

            display: "grid",

            placeItems: "center",

            background:
              "linear-gradient(145deg, #ffffff, #e0f2fe)",

            border:
              "1px solid rgba(37,99,235,0.16)",

            boxShadow:
              "0 12px 26px rgba(23,32,51,0.12)",

            flexShrink: 0,

          }}
        >

          <Avatar
            src={schoolLogo}
            alt="school logo"
            onError={(event) => {

              event.currentTarget.src =
                defaultLogo;

            }}
            sx={{

              width: "100%",

              height: "100%",

              borderRadius: 1.4,

              bgcolor: "#ffffff",

            }}
          />

        </Box>


        {/* ==================================================
            ONLY DAY COUNT
        =================================================== */}

        {!subscriptionLoading &&
          remainingDays !== null && (

            <Box
              sx={{

                display: "flex",

                alignItems: "center",

                gap: 0.7,

                ml: {
                  xs: 0.3,
                  sm: 0.6,
                },

                px: {
                  xs: 0.8,
                  sm: 1.1,
                },

                py: {
                  xs: 0.55,
                  sm: 0.65,
                },

                borderRadius: 999,

                bgcolor:
                  "rgba(255,255,255,0.92)",

                border:
                  "1px solid rgba(37,99,235,0.14)",

                boxShadow:
                  "0 8px 20px rgba(37,99,235,0.09)",

                flexShrink: 0,

              }}
            >

              {/* CLOCK ICON */}

              <AccessTimeIcon
                sx={{

                  fontSize: {
                    xs: 18,
                    sm: 20,
                  },

                  color: "#2563eb",

                }}
              />


              {/* NUMBER */}

              <Typography
                sx={{

                  fontSize: {
                    xs: "0.95rem",
                    sm: "1.05rem",
                  },

                  fontWeight: 900,

                  color: "#102a43",

                  lineHeight: 1,

                  letterSpacing: "-0.02em",

                }}
              >

                {remainingDays}

              </Typography>


              {/* DAYS */}

              <Typography
                sx={{

                  fontSize: {
                    xs: "0.45rem",
                    sm: "0.5rem",
                  },

                  fontWeight: 900,

                  color: "#64748b",

                  lineHeight: 1,

                  textTransform:
                    "uppercase",

                }}
              >

                DAYS

              </Typography>

            </Box>

          )}


        {/* LOADING */}

        {subscriptionLoading && (

          <Box
            sx={{

              display: {
                xs: "none",
                sm: "flex",
              },

              alignItems: "center",

              px: 1,

              py: 0.6,

              borderRadius: 999,

              bgcolor:
                "rgba(255,255,255,0.85)",

              border:
                "1px solid rgba(37,99,235,0.10)",

            }}
          >

            <Typography
              sx={{

                fontSize: "0.5rem",

                fontWeight: 800,

                color: "#64748b",

              }}
            >

              LOADING...

            </Typography>

          </Box>

        )}

      </Box>


      {/* =====================================================
          CENTER SCHOOL NAME
      ====================================================== */}

      <Box
        sx={{

          position: "absolute",

          left: "50%",

          transform:
            "translateX(-50%)",

          width: {
            xs: "38%",
            sm: "42%",
            md: "45%",
          },

          display: "flex",

          justifyContent: "center",

          pointerEvents: "none",

          zIndex: 1,

        }}
      >

        <Typography
          variant="h6"
          noWrap
          sx={{

            maxWidth: "100%",

            px: {
              xs: 1.5,
              sm: 2.5,
            },

            py: {
              xs: 0.65,
              sm: 0.8,
            },

            borderRadius: 999,

            fontWeight: 900,

            fontSize: {
              xs: "0.72rem",
              sm: "0.95rem",
              md: "1.1rem",
            },

            color: "#102a43",

            textTransform:
              "uppercase",

            background:
              "linear-gradient(90deg, rgba(255,255,255,0.9), rgba(224,242,254,0.92), rgba(236,253,245,0.9))",

            border:
              "1px solid rgba(37,99,235,0.12)",

            boxShadow:
              "inset 0 1px 0 rgba(255,255,255,0.9), 0 10px 28px rgba(37,99,235,0.08)",

            overflow: "hidden",

            textOverflow:
              "ellipsis",

          }}
        >

          {schoolName ||
            "SCHOOL PORTAL"}

        </Typography>

      </Box>


      {/* =====================================================
          RIGHT SECTION
      ====================================================== */}

      <Box
        sx={{

          display: "flex",

          alignItems: "center",

          justifyContent: "flex-end",

          gap: {
            xs: 0.7,
            sm: 1,
          },

          position: "relative",

          zIndex: 3,

          flexShrink: 0,

          ml: "auto",

        }}
      >

        {/* ==================================================
            FULL USER NAME
        =================================================== */}

        {!isMobile && (

          <Typography
            variant="body2"
            sx={{

              px: {
                sm: 1.2,
                md: 1.5,
              },

              py: 0.8,

              borderRadius: 999,

              fontWeight: 850,

              color: "#102a43",

              bgcolor:
                "rgba(255,255,255,0.76)",

              border:
                "1px solid rgba(37,99,235,0.12)",

              boxShadow:
                "0 10px 24px rgba(23,32,51,0.08)",

              /*
               * IMPORTANT:
               * Name ar maxWidth nei
               * noWrap nei
               * tai full name kete jabe na
               */

              maxWidth: "none",

              width: "auto",

              whiteSpace: "nowrap",

              overflow: "visible",

              flexShrink: 0,

            }}
          >

            {userName || "User"}

          </Typography>

        )}


        {/* ==================================================
            PROFILE BUTTON
        =================================================== */}

        <Tooltip title="Account">

          <IconButton
            onClick={handleMenuOpen}
            sx={{

              p: 0.35,

              border:
                "1px solid rgba(37,99,235,0.16)",

              bgcolor:
                "rgba(255,255,255,0.86)",

              boxShadow:
                "0 12px 26px rgba(23,32,51,0.12)",

              flexShrink: 0,

            }}
          >

            <Avatar
              src={
                avatarSrc || undefined
              }
              alt={
                userName || "user"
              }
              sx={{

                width: {
                  xs: 38,
                  sm: 42,
                },

                height: {
                  xs: 38,
                  sm: 42,
                },

                bgcolor: "#2563eb",

                border:
                  "2px solid #dbeafe",

              }}
            >

              {!avatarSrc && (

                <AccountCircleIcon />

              )}

            </Avatar>

          </IconButton>

        </Tooltip>


        {/* ==================================================
            MENU
        =================================================== */}

        <Menu
          anchorEl={anchorEl}
          open={Boolean(anchorEl)}
          onClose={handleMenuClose}
          anchorOrigin={{
            vertical: "bottom",
            horizontal: "right",
          }}
          transformOrigin={{
            vertical: "top",
            horizontal: "right",
          }}
          PaperProps={{
            sx: {

              minWidth: 210,

              borderRadius: 2,

              overflow: "hidden",

              border:
                "1px solid rgba(78,102,135,0.16)",

              boxShadow:
                "0 18px 46px rgba(23,32,51,0.16)",

            },
          }}
        >

          {/* USER INFO */}

          <Box
            sx={{

              px: 2,

              py: 1.25,

            }}
          >

            <Typography
              variant="subtitle2"
              sx={{

                fontWeight: 700,

                color:
                  "text.primary",

                whiteSpace:
                  "normal",

                wordBreak:
                  "break-word",

              }}
            >

              {userName || "User"}

            </Typography>


            <Typography
              variant="caption"
              sx={{
                color:
                  "text.secondary",
              }}
            >

              {localStorage.getItem(
                "role"
              ) || ""}

            </Typography>

          </Box>


          <Divider />


          {/* PROFILE */}

          <MenuItem
            sx={{
              gap: 1,
            }}
          >

            <AccountCircleIcon
              fontSize="small"
            />

            <Typography
              variant="body2"
            >

              Profile

            </Typography>

          </MenuItem>


          {/* LOGOUT */}

          <MenuItem
            onClick={handleLogout}
            sx={{
              gap: 1,
            }}
          >

            <LogoutIcon
              fontSize="small"
            />

            <Typography
              variant="body2"
            >

              Logout

            </Typography>

          </MenuItem>

        </Menu>

      </Box>

    </Box>

  );

}